import { filter, isNull, noop } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import AssetsAPI from "restAPI/AssetAPI";
import { AssetEsDto } from "types/assetdetails";
import { ASSET_EVENTS_MAP } from "utils/assetUtils";
import Sorters from "utils/sorters";

import { HistoryResponse } from "./asset-history";

type AssetDetailsContextType = {
  assetDetails: AssetEsDto;
  loading: boolean;
  assetID: number;
  refreshDetails: () => void;
  history: HistoryResponse[];
};

const DummyAssetEsDto = {
  id: 0,
  name: "",
  source: [],
  toolType: [],
  armorCodeType: "",
  assetIdFromTool: "",
  toolLoginId: null,
  active: null,
  firstSeen: null,
  lastSeen: null,
  deletedDate: null,
  tags: [],
  tagsMetaMap: null,
  otherProperties: null,
  dnsName: [],
  cloudProvider: [],
  os: [],
  osVersion: [],
  hostname: [],
  ipv4: [],
  ipv6: [],
  cloudAccountId: [],
  arn: [],
  macAddress: [],
  location: [],
  owner: "",
  hierarchy: null,
  registry: [],
  cluster: [],
  namespace: [],
  scanned: null,
  cloudResourceProperties: {
    cloudService: "",
    runtime: [],
    architecture: [],
    vpcId: [],
    region: [],
    publiclyAccessible: null,
    role: [],
    version: [],
    storageType: [],
    engineVersion: [],
    engine: [],
    instanceStatus: [],
    subnetIds: [],
    name: "",
    imageNames: [],
    nativeTypes: [],
    kubernetesFlavor: [],
    authenticationMode: [],
  },
  repositoryProperties: {
    repositorySubType: "",
    nativeTypes: [],
    architecture: [],
  },
  hostProperties: {
    hostSubType: "",
    nativeTypes: [],
    kubernetesFlavor: [],
    authenticationMode: [],
    kubernetesVersion: [],
    kubernetesClusterName: [],
    architecture: [],
    region: [],
  },
  networkResourceProperties: {
    networkResourceType: [],
  },
  codeRepo: {
    id: null,
    repositoryName: "",
    repositoryUrl: "",
  },
};

const AssetsDetailsContext = React.createContext<AssetDetailsContextType>({
  assetDetails: DummyAssetEsDto,
  loading: false,
  assetID: 0,
  refreshDetails: noop,
  history: [],
});

type Props = {
  children?: JSX.Element[] | JSX.Element;
  mainModalRef: React.ForwardedRef<any>;
  assetID: number;
};

export default function AssetsDetailsProvider({ children, assetID }: Props) {
  const [assetDetails, setAssetDetails] = useState<AssetEsDto>(DummyAssetEsDto);
  const [history, setHistory] = useState<HistoryResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAssetDetails = useCallback((armorcodeAssetId) => {
    setLoading(true);
    const { promise, cancel } = AssetsAPI.getAssetDetails(armorcodeAssetId);
    promise
      .then((res) => setAssetDetails(res))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
    return cancel;
  }, []);

  const fetchHistory = useCallback((assetID) => {
    const { promise, cancel } = AssetsAPI.getHistory(assetID);
    promise.then((response) => {
      const sortedHistory = [
        ...filter(
          response.content,
          (item) =>
            !isNull(item.event) &&
            ASSET_EVENTS_MAP[item.event as keyof typeof ASSET_EVENTS_MAP],
          //@ts-ignore
        ).sort(Sorters.DATE("createdAt", true)),
      ];
      setHistory(sortedHistory as HistoryResponse[]);
    });
    return cancel;
  }, []);

  useEffect(() => {
    fetchAssetDetails(assetID);
    fetchHistory(assetID);
  }, [fetchAssetDetails, fetchHistory, assetID]);

  const refreshDetails = useCallback(() => {
    fetchAssetDetails(assetID);
    fetchHistory(assetID);
  }, [assetID, fetchAssetDetails, fetchHistory]);

  const contextValue = useMemo(() => {
    return {
      loading,
      assetDetails,
      assetID,
      refreshDetails,
      history,
    };
  }, [loading, assetDetails, assetID, refreshDetails, history]);

  return (
    <AssetsDetailsContext.Provider value={contextValue}>
      {children}
    </AssetsDetailsContext.Provider>
  );
}

export function useAssetsDetails() {
  return useContext(AssetsDetailsContext);
}
